import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import AuthSplitLayout from './AuthSplitLayout';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';

/**
 * Reset-password landing page. The reset email links here (frontend origin) with a `token`; unlike
 * magic-link/verify-email (a single GET), completing a reset needs the user to enter a new password,
 * so this is a form that POSTs `{ newPassword, token }` to `/auth/reset-password`. Must be a PUBLIC
 * route: the visitor is unauthenticated, and a private route would trip the session query → Apollo
 * bounces to `/auth/login?redirectTo=…`.
 */
export default function AuthResetPassword() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const token = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('token') || '';
    }, []);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (newPassword !== confirmPassword) {
            enqueueSnackbar(t('auth.reset-password.mismatch'), { variant: 'error' });
            return;
        }
        setLoading(true);
        try {
            await authApi.resetPassword({ newPassword, token });
            setDone(true);
            enqueueSnackbar(t('auth.reset-password.success'), { variant: 'success' });
        } catch (error) {
            const mapped = mapAuthError(error, t, 'reset-password');
            enqueueSnackbar(mapped.message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const backToLogin = (
        <Box>
            <Typography
                component={Link}
                to="/auth/login"
                variant="body2"
                color="text.secondary"
                sx={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { color: 'primary.main' },
                }}
            >
                <ArrowBackRounded sx={{ fontSize: 16 }} />
                {t('auth.forgot-password.back-to-login')}
            </Typography>
        </Box>
    );

    // Malformed link (no token): don't render the form, send the user back to request a new one.
    if (!token) {
        return (
            <AuthSplitLayout>
                <Stack spacing={3}>
                    {backToLogin}
                    <Box>
                        <Typography variant="h5" fontWeight={700} mb={0.5}>
                            {t('auth.reset-password.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.reset-password.invalid-link')}
                        </Typography>
                    </Box>
                    <Button
                        component={Link}
                        to="/auth/forgot-password"
                        variant="contained"
                        size="large"
                        fullWidth
                    >
                        {t('auth.forgot-password.submit')}
                    </Button>
                </Stack>
            </AuthSplitLayout>
        );
    }

    return (
        <AuthSplitLayout>
            <Stack spacing={3}>
                {backToLogin}

                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.reset-password.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.reset-password.subheading')}
                    </Typography>
                </Box>

                {done ? (
                    <>
                        <Box
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'success.light',
                                color: 'success.dark',
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body1" fontWeight={600}>
                                ✅ {t('auth.reset-password.success')}
                            </Typography>
                        </Box>
                        <Button
                            component={Link}
                            to="/auth/login"
                            variant="contained"
                            size="large"
                            fullWidth
                        >
                            {t('auth.forgot-password.back-to-login')}
                        </Button>
                    </>
                ) : (
                    <Stack component="form" spacing={2} onSubmit={onSubmit}>
                        <TextField
                            label={t('auth.reset-password.new-password')}
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            autoFocus
                            autoComplete="new-password"
                            inputProps={{ minLength: 8 }}
                            fullWidth
                        />
                        <TextField
                            label={t('auth.reset-password.confirm-password')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            inputProps={{ minLength: 8 }}
                            fullWidth
                        />
                        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
                            {loading ? t('auth.common.loading') : t('auth.reset-password.submit')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </AuthSplitLayout>
    );
}
